import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {InputTextarea} from 'primereact/inputtextarea'
import {Tag} from 'primereact/tag'
import {forwardRef, useImperativeHandle, useRef} from 'react'
import {ReactComponent as MailIcon} from '../../assets/icons/MailIcon.svg'
import {ReactComponent as TbDeviceWatch} from '../../assets/icons/TbDeviceWatch.svg'
import {ReactComponent as VoiceIcon} from '../../assets/icons/VoiceIcon.svg'
import {ReactComponent as SmsIcon} from '../../assets/icons/smsIcon.svg'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import ImageUploader from '../image/ImageUploader'
import {severityNotification} from '../notification/NotificationConstant'
import {sendPushNotification} from '../notification/NotificationEndPoint'
import {showNotification} from '../notification/NotificationService'
import {sendMethodTypes} from '../reminder/ReminderConstants'
import {sendMessageViaEmail, sendMessageViaSms} from './MessageEndpoint'
const iconStyle = {
    width: '27px',
    height: '27px',
    color: '#6c757d',
    marginRight: '6px',
}

const sendMessageKeys = {
    method: 'method',
    message: 'message',
}

const SendMessageForm = forwardRef(({}, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    const {user} = useAuth()

    const data = useSignal({})
    const visible = useSignal(false)

    const getIconStyled = type => {
        let style = {...iconStyle}
        if (type === data.value[sendMessageKeys.method]) {
            style.color = 'darkcyan'
        }
        return style
    }
    const sendMethodList = [
        {
            label: 'Email',
            icon: <MailIcon style={getIconStyled(sendMethodTypes.email)} />,
            type: sendMethodTypes.email,
            command: () => {},
            disable: !data.value?.email,
        },
        {
            label: 'SMS',
            icon: <SmsIcon style={getIconStyled(sendMethodTypes.sms)} />,
            type: sendMethodTypes.sms,
            command: () => {},
            disable: !data.value?.phone || data.value?.phone?.length < 10,
        },
        {
            label: 'Voicemail',
            icon: <VoiceIcon style={getIconStyled(sendMethodTypes.voice)} />,
            type: sendMethodTypes.voice,
            command: () => {},
            disable: !data.value?.phone || data.value?.phone?.length < 10,
        },
        {
            label: 'Push Notification',
            icon: (
                <TbDeviceWatch
                    style={getIconStyled(sendMethodTypes.pushNotification)}
                />
            ),
            type: sendMethodTypes.pushNotification,
            command: () => {},
            disable: !data.value?.deviceAssigned,
        },
    ]
    const handleShowForm = patientInfo => {
        blockOverflownScroll()
        visible.value = true
        data.value = {
            ...patientInfo,
            [sendMessageKeys.method]: sendMethodTypes.email,
            [sendMessageKeys.message]: '',
        }
    }
    const handleHideForm = () => {
        unblockOverflownScroll()
        visible.value = false
    }

    const handleOnChange = (key, value) => {
        data.value = {...data.value, [key]: value}
    }

    const selectedStyled = {
        background: 'rgba(0, 139, 139, 0.05)',
        borderColor: 'darkcyan',
        color: 'darkcyan',
    }
    const availableStyled = {
        borderColor: 'var(--gray-200)',
        color: 'var(--gray-600)',
    }
    const unavailableStyled = {
        borderColor: 'var(--gray-200)',
        color: 'var(--gray-600)',
        opacity: 0.67,
    }

    const imageRef = useRef(null)

    const handleSendMessage = async () => {
        if (data.value[sendMessageKeys.method] === sendMethodTypes.sms) {
            await sendMessageViaSms(data.value)
        }

        if (data.value[sendMessageKeys.method] === sendMethodTypes.email) {
            await sendMessageViaEmail({
                ...data.value,
                to: data.value?.email,
                template: 'message.vm',
                subject: `TaVIE - Message from ${user?.fullName}`,
                data: {
                    content: `
                    <div style="font-family: Arial, sans-serif; color: #333; line-height: 1.6; margin: 0; padding: 0;">
                        <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #f4f4f4;">
                            <tr>
                                <td align="center">
                                    <table width="600" cellpadding="0" cellspacing="0" border="0" style="background-color: #ffffff; padding: 20px; border-radius: 8px;">
                                        <tr>
                                            <td style="padding: 20px; text-align: center;">
                                                <h3 style="font-size: 20px; color: #333;">Hello ${
                                                    data.value?.fullName
                                                },</h3>
                                                <p style="font-size: 16px; color: #555;">
                                                    Here is the message content sent from ${
                                                        user?.fullName
                                                    }:
                                                </p>
                                                <div style="background-color: #f9f9f9; padding: 15px; border: 1px solid #ddd; border-radius: 5px;">
                                                    <p style="font-size: 14px; color: #333;">${
                                                        data.value[
                                                            sendMessageKeys
                                                                .message
                                                        ]
                                                    }</p>
                                                </div>
                                                <p style="font-size: 14px; color: #777;">
                                                    Please do not reply to this email. If you have any questions, feel free to contact our support team.
                                                </p>
                                            </td>
                                        </tr>
                                    </table>
                                </td>
                            </tr>
                        </table>
                    </div>
                    `,
                },
            })
        }

        if (
            data.value[sendMessageKeys.method] ===
            sendMethodTypes.pushNotification
        ) {
            let toPostData = {
                userIds: [data.value?.id],
                body: data.value[sendMessageKeys.message] || '',
                image: imageRef.current.value || '',
            }

            await sendPushNotification(toPostData, data.value?.id)
        }

        showNotification(severityNotification.success, 'Message delivered !!')

        handleHideForm()
    }

    return (
        <Dialog
            header="Send Message Form"
            visible={visible.value}
            blockScroll
            onHide={handleHideForm}>
            <div>
                <div className="col-12">
                    <h5 className="font-bold">Send Method</h5>
                    <p>
                        Please select the available method you want to send the
                        message
                    </p>
                    <div className="mb-3">
                        <div className="grid px-0">
                            {sendMethodList.map(
                                (item, idx) =>
                                    !item?.disable && (
                                        <div className="col-3" key={idx}>
                                            <div
                                                className={`tooltip-${
                                                    item.type + idx
                                                } p-3 border-1 border-round-lg text-center ${
                                                    !item.disable &&
                                                    ' hover:bg-gray-50 cursor-pointer'
                                                }`}
                                                style={
                                                    item.disable
                                                        ? unavailableStyled
                                                        : data.value[
                                                              sendMessageKeys
                                                                  .method
                                                          ] === item.type
                                                        ? selectedStyled
                                                        : availableStyled
                                                }
                                                onClick={() =>
                                                    !item.disable &&
                                                    handleOnChange(
                                                        sendMessageKeys.method,
                                                        item.type,
                                                    )
                                                }>
                                                <div> {item.icon}</div>
                                                <h6 className="font-semibold m-0 p-2">
                                                    {' '}
                                                    {item.label}
                                                </h6>
                                            </div>
                                        </div>
                                    ),
                            )}
                        </div>
                        {data.value[sendMessageKeys.method] ===
                            sendMethodTypes.email && (
                            <div className="flex gap-2 mb-2 font-bold text-darkcyan">
                                Send to : {data.value?.email}
                            </div>
                        )}
                        {(data.value[sendMessageKeys.method] ===
                            sendMethodTypes.sms ||
                            data.value[sendMessageKeys.method] ===
                                sendMethodTypes.voice) && (
                            <div className="flex gap-2 mb-2 font-bold text-darkcyan">
                                Send to : {data.value?.phone}
                            </div>
                        )}
                        {data.value[sendMessageKeys.method] ===
                            sendMethodTypes.pushNotification && (
                            <div className="flex gap-2 mb-2 font-bold text-darkcyan">
                                Send to : {data.value?.watchDeviceName}
                            </div>
                        )}
                    </div>

                    <div className="flex align-items-center justify-content-between">
                        <div>
                            <h5 className="font-bold">Message</h5>
                        </div>
                        <div>
                            <Tag
                                value={'This field is required !'}
                                severity="danger"
                                icon="pi pi-info-circle"></Tag>
                        </div>
                    </div>

                    <p>
                        Please enter the message you want to send to the patient
                    </p>
                    <div className="flex gap-2 mb-2">
                        <InputTextarea
                            value={data.value[sendMessageKeys.message]}
                            placeholder="Type your message here..."
                            className="col-12"
                            rows={6}
                            onChange={e =>
                                handleOnChange(
                                    sendMessageKeys.message,
                                    e.target.value,
                                )
                            }
                        />
                    </div>
                </div>

                {data.value[sendMessageKeys.method] ===
                    sendMethodTypes.pushNotification && (
                    <div className="col-12">
                        <h5 className="font-bold">Image (Optional)</h5>
                        <ImageUploader
                            id={'image'}
                            ref={imageRef}
                            callBack={() => {}}
                            multiple={false}
                            extraData={{}}
                        />
                    </div>
                )}

                <div className="grid col-12">
                    <Button
                        icon="pi pi-send"
                        className="w-full h-3rem"
                        label="SUBMIT"
                        onClick={handleSendMessage}
                    />
                </div>
            </div>
        </Dialog>
    )
})

export default SendMessageForm
